import { useQuery } from '@tanstack/react-query';
import { Button, Form, Space } from 'antd';
import PageTitle from 'apps/home/components/PageTitle';
import DataTable from 'common/ui/DataTable';
import { useCallback, useEffect, useMemo } from 'react';
import {
  Link,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import SearchForm from './SearchForm';

const AdminList = ({
  title,
  columnGenerator,
  apiCall,
  createUrl,
  listProps,
  resourceName,
  enableSearch = false,
  searchOptions = {},
  FilterForm,
  showBreadscrumb = true,
  extra,
  params: externalParams,
  enabled = true,
  ...props
}) => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const pageParams = useParams();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const queryParams = Object.fromEntries(new URLSearchParams(location.search));

  // Merge all params
  const allParams = useMemo(
    () => ({ ...pageParams, ...queryParams, ...externalParams }),
    [pageParams, queryParams, externalParams]
  );

  const {
    data: response,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: [apiCall.list.queryKey, allParams],
    queryFn: () => apiCall.list.queryFn(allParams),
    enabled,
  });

  const items = response?.[resourceName || 'items'] || [];
  const paging = response?.paging;

  // Update query params from search params
  useEffect(() => {
    if (searchParams.get('page')) {
      queryParams.page = searchParams.get('page');
    }
    if (searchParams.get('search')) {
      queryParams.search = searchParams.get('search');
    }
    if (searchParams.get('ordering')) {
      queryParams.ordering = searchParams.get('ordering');
    }
  }, [searchParams]);

  const fetchItems = useCallback(
    newParams => {
      const mergedParams = { ...allParams, ...newParams };
      const queryString = new URLSearchParams(
        Object.fromEntries(
          Object.entries(mergedParams).filter(([, v]) => v != null && v !== '')
        )
      ).toString();
      navigate(location.pathname + (queryString ? `?${queryString}` : ''));
    },
    [allParams, location.pathname, navigate]
  );

  const onFiltering = values => {
    console.log('Received values of form: ', values);

    const payload = Object.fromEntries(
      Object.entries(values).filter(([, v]) => v)
    );
    const queryString = new URLSearchParams(payload).toString();
    navigate(location.pathname + `?${queryString}`);
  };

  return (
    <>
      <PageTitle
        title={title}
        showBreadscrumb={showBreadscrumb}
        extra={
          <>
            {extra}
            {createUrl && [
              <Button key="1" type="primary">
                <Link to={createUrl}>Create</Link>
              </Button>,
            ]}
          </>
        }
      />
      <Space direction="vertical">
        {enableSearch ? <SearchForm {...searchOptions} /> : null}
        {FilterForm && (
          <FilterForm
            form={form}
            onFinish={onFiltering}
            submiting={isLoading}
            initialValues={queryParams}
            name="filter_form"
          />
        )}
      </Space>
      <br />
      {props.warningMessages &&
        props.warningMessages.map((item, index) => item)}
      <DataTable
        rowKey="id"
        columns={columnGenerator(listProps)}
        dataSource={items}
        totalItems={paging?.count}
        currentPage={paging?.page}
        defaultCurrent={1}
        defaultPageSize={paging?.page_size}
        pageSize={paging?.page_size}
        dataFetcher={fetchItems}
        loading={isLoading}
        filters={allParams}
        {...props}
      />
    </>
  );
};

export default AdminList;
